import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { STAFF } from "@/constants/testIds";
import { toast } from "sonner";
import { Plus, Save, Trash2, UserCog, X, ShieldAlert } from "lucide-react";
import ExportButton from "@/components/ExportButton";
import SearchBar, { useLocalFilter } from "@/components/SearchBar";
import { useAuth } from "@/contexts/AuthContext";

const ROLE_COLORS = {
  super_admin: "bg-orange-50 text-orange-700 border-orange-200",
  sales_agent: "bg-indigo-50 text-indigo-700 border-indigo-200",
  viewer: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function Team() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(null);
  const [q, setQ] = useState("");
  const [armed, setArmed] = useState("");  // staff-id awaiting second click
  const filtered = useLocalFilter(users, q, ["name", "email", "role"]);
  const load = async () => setUsers((await api.get("/staff")).data);
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!creating.email || !creating.name || !creating.password) return toast.error("All fields required");
    try {
      await api.post("/staff", creating);
      toast.success("User created");
      setCreating(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const remove = async (id) => {
    if (armed !== id) {
      setArmed(id);
      toast.warning("Click the trash icon again within 4s to confirm deletion");
      setTimeout(() => setArmed((prev) => (prev === id ? "" : prev)), 4000);
      return;
    }
    setArmed("");
    try {
      await api.delete(`/staff/${id}`);
      toast.success("Team member deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid={STAFF.root} className="p-8 lg:p-12 max-w-[1000px]">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 mb-1">Team</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-2">
            <UserCog className="h-7 w-7 text-orange-600" /> Team &amp; Roles
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            <b>Super Admin</b> — full access · <b>Sales Agent</b> — inbox + drafts · <b>Viewer</b> — read-only
          </p>
        </div>
        <div className="flex gap-2">
          <ExportButton path="/exports/team.xlsx" filename="team.xlsx" testId="team-export-btn" />
          {isAdmin && (
            <button
              data-testid={STAFF.addBtn}
              onClick={() => setCreating({ email: "", name: "", password: "", role: "sales_agent" })}
              className="inline-flex items-center gap-2 bg-orange-600 text-white text-xs font-semibold px-3 py-2 rounded-md hover:bg-orange-500 transition-colors duration-150"
            >
              <Plus className="h-3.5 w-3.5" /> Invite user
            </button>
          )}
        </div>
      </div>

      <div className="mb-4"><SearchBar value={q} onChange={setQ} placeholder="Search team…" testId="team-search" /></div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Name</th>
              <th className="text-left px-4 py-3 font-semibold">Email</th>
              <th className="text-left px-4 py-3 font-semibold">Role</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} data-testid={`${STAFF.rowPrefix}${u.id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors duration-150">
                <td className="px-4 py-3 font-semibold">{u.name}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded border ${ROLE_COLORS[u.role]}`}>
                    {u.role.replace("_", " ")}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`h-2 w-2 rounded-full inline-block ${u.active ? "bg-emerald-500" : "bg-slate-300"}`} />
                </td>
                <td className="px-4 py-3 text-right">
                  {isAdmin ? (
                    u.id === user?.id ? (
                      <span className="text-[10px] text-slate-400 italic">you</span>
                    ) : (
                      <button
                        onClick={() => remove(u.id)}
                        data-testid={`${STAFF.rowPrefix}${u.id}-delete-btn`}
                        title={armed === u.id ? "Click again to confirm" : "Delete team member"}
                        className={`inline-flex items-center gap-1 text-xs font-semibold rounded px-2 py-1 border transition-colors duration-150 ${
                          armed === u.id
                            ? "bg-red-600 text-white border-red-600 animate-pulse"
                            : "text-slate-500 hover:text-red-600 border-transparent hover:border-red-200"
                        }`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        {armed === u.id ? "Confirm" : ""}
                      </button>
                    )
                  ) : (
                    <span title="Only super_admin can delete team members" className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                      <ShieldAlert className="h-3 w-3" /> admin only
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setCreating(null)}>
          <div className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg tracking-tight">Invite staff</h3>
              <button onClick={() => setCreating(null)}><X className="h-4 w-4 text-slate-500" /></button>
            </div>
            <div className="space-y-3">
              <input data-testid={STAFF.nameInput} placeholder="Name" value={creating.name}
                onChange={(e) => setCreating({ ...creating, name: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none" />
              <input data-testid={STAFF.emailInput} type="email" placeholder="Email" value={creating.email}
                onChange={(e) => setCreating({ ...creating, email: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none" />
              <input data-testid={STAFF.passwordInput} type="password" placeholder="Password (min 6)" value={creating.password}
                onChange={(e) => setCreating({ ...creating, password: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none" />
              <select data-testid={STAFF.roleSelect} value={creating.role}
                onChange={(e) => setCreating({ ...creating, role: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none">
                <option value="super_admin">Super Admin</option>
                <option value="sales_agent">Sales Agent</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setCreating(null)} className="text-xs px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50">Cancel</button>
              <button data-testid={STAFF.saveBtn} onClick={create} className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md bg-orange-600 text-white font-semibold hover:bg-orange-500 transition-colors duration-150">
                <Save className="h-3.5 w-3.5" /> Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
